import type { PLAN_NAME } from '@readme/plans';

import { getPlanFeature } from '@readme/plans';

export interface AiWriterAccessFlags {
  aiWriter?: boolean;
  hideAiFeatures?: boolean;
}

export interface AiWriterAccessParent {
  flags?: AiWriterAccessFlags;
  plan?: PLAN_NAME | string;
  planOverride?: PLAN_NAME | '' | string;
}

export interface AiWriterAccessInput {
  flags?: AiWriterAccessFlags;
  parent?: AiWriterAccessParent | null;
  plan?: PLAN_NAME | string;
  planOverride?: PLAN_NAME | '' | string;
}

/**
 * Checks whether the project has access to the AI writer.
 *
 * Order of evaluation:
 * 1. `flags.hideAiFeatures` always wins → no access (compliance opt-out).
 * 2. Enterprise projects always deffer to parent project settings
 * 3. `flags.aiWriter` is a universal godmode override → access on any plan.
 * 4. Otherwise the plan must include the `ai_writer` feature.
 */
export function canUseAiWriter(input: AiWriterAccessInput): boolean {
  if (input.flags?.hideAiFeatures) return false;

  if (input.parent) {
    return canUseAiWriter({
      flags: input.parent.flags,
      plan: input.parent.plan,
      planOverride: input.parent.planOverride,
    });
  }

  if (input.flags?.aiWriter) return true;

  const plan = input.planOverride || input.plan;
  if (!plan) return false;

  return !!getPlanFeature(plan as PLAN_NAME, 'ai_writer');
}
